import { FormControlLabel, FormHelperText, Radio, RadioProps } from '@mui/material';
import useHelperTextId from '@platform-ui-common/hooks/contexts/useHelperTextId.js';
import cx from 'classnames';
import React, { ReactNode } from 'react';
import Typography from '../Typography/index.js'; // Relative import to avoid circular dependencies

function BpIcon({ disabled, error = false }: { disabled: boolean; error?: boolean }): JSX.Element {
  let shadow = disabled ? 'shadow-[0_0_0_1px_#909090]' : 'shadow-[0_0_0_1px_#212121]';
  if (error) shadow = 'shadow-[0_0_0_1px_#BE2E24]';
  return <span className={cx('rounded-full w-[18px] h-[18px]', shadow)} />;
}

function BpCheckedIcon({ disabled }: { disabled: boolean }): JSX.Element {
  return (
    <span
      className={cx(
        'mt-1 mx-1 rounded-full bg-[#FFF] bg-[linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))]',
        'before:block before:w-[10px] before:h-[10px] before:bg-[radial-gradient(#fff,#fff 35%,transparent 39%)]',
        disabled ? 'shadow-[0_0_0_5px_#909090]' : 'shadow-[0_0_0_5px_#212121]',
      )}
    />
  );
}

function BpRadio(props: RadioProps & { readonly: boolean }): JSX.Element {
  const { disabled, readonly, ...rest } = props;
  return (
    <Radio
      disableRipple={readonly}
      disabled={disabled}
      color="default"
      checkedIcon={<BpCheckedIcon disabled={disabled} />}
      icon={<BpIcon disabled={disabled} />}
      {...rest}
    />
  );
}

type Value = string | number;

interface RadioOption {
  label: ReactNode;
  description?: ReactNode;
  value: Value;
  disabled?: boolean;
}

interface RadioGroupProps {
  // Should be the id of the elements that describe the group:
  // - label: used for aria-labelledby, should be brief text that provides
  //   the element with an accessible name
  // - description: used for aria-describedby, if there is longer content
  //   that provides a description
  ariaTags?: { label: string; description?: string };
  error?: boolean;
  helperText?: string;
  items: RadioOption[];
  name: string;
  onChange?: (value: Value) => void;
  readonly?: boolean;
  selectedValue?: Value;
}

type HTMLMouseChangeEvent = React.ChangeEvent<HTMLInputElement>;
export default function RadioGroup({
  ariaTags,
  error = false,
  helperText,
  items,
  name,
  onChange,
  readonly = false,
  selectedValue,
}: RadioGroupProps): JSX.Element {
  const handleChange = (value: Value): void => {
    if (onChange && !readonly) onChange(value);
  };
  const helperTextId = useHelperTextId();

  return (
    <>
      <div
        className="w-full flex flex-auto flex-col gap-3"
        role="radiogroup"
        {...(ariaTags?.label && { 'aria-labelledby': ariaTags.label })}
        {...(ariaTags?.description && { 'aria-describedby': ariaTags.description })}
      >
        {items.map(({ label, value, description, disabled }) => {
          const wrapperStyles = {
            'bg-lightBackground': value === selectedValue,
            'border-error': error,
            'border-inputBorder': !error,
          };
          return (
            <div className={cx('flex border', wrapperStyles)} key={value}>
              <FormControlLabel
                value={value}
                checked={selectedValue === value}
                onChange={(e: HTMLMouseChangeEvent): void => handleChange(e.target.value)}
                control={
                  <BpRadio
                    id={`${name}-${value}`}
                    name={name}
                    disabled={disabled}
                    readonly={readonly}
                    inputProps={{
                      readOnly: readonly,
                      'aria-errormessage': helperTextId,
                      'aria-invalid': error,
                    }}
                    data-cy={`${value}-option`}
                  />
                }
                label={
                  <span className="flex flex-col gap-1">
                    <Typography variant="body" tag="span" bold={!!description}>
                      {label}
                    </Typography>
                    {description && (
                      <Typography variant="body" tag="span">
                        {description}
                      </Typography>
                    )}
                  </span>
                }
                key={`radioButton-${value}`}
                disabled={disabled}
              />
            </div>
          );
        })}
      </div>
      {!!error && (
        <FormHelperText error={error} id={helperTextId}>
          {helperText}
        </FormHelperText>
      )}
    </>
  );
}
